import React, { useEffect, useRef } from 'react';
import './NotificationPreview.css';
import { StatusBadge } from '@medprompt/shared';
import { getViolationByValue } from '../enums/lmcContractViolationEnum';

/**
 * Opens upward only when the downward card crosses the bottom of the table
 * scroll box and the same card still fits above the badge, under the sticky header.
 * .table-scroll-wrapper is the clip: overflow:auto there, not on the cell.
 */
const updateOpenAbove = (wrapper) => {
  const card = wrapper.querySelector('.notif-preview-card');
  const scroll = wrapper.closest('.table-scroll-wrapper');
  if (!card || !scroll) {
    wrapper.classList.remove('open-above');
    return;
  }

  wrapper.classList.remove('open-above');

  const wrapperRect = wrapper.getBoundingClientRect();
  const scrollRect = scroll.getBoundingClientRect();
  const scrollStyle = getComputedStyle(scroll);
  const clipTop = scrollRect.top + (parseFloat(scrollStyle.borderTopWidth) || 0);
  const clipBottom = scrollRect.bottom - (parseFloat(scrollStyle.borderBottomWidth) || 0);
  const cardBottom = wrapperRect.top + card.offsetTop + card.offsetHeight;
  if (cardBottom <= clipBottom) {
    return;
  }

  const header = scroll.querySelector('.table-header');
  const ceiling = header ? Math.max(clipTop, header.getBoundingClientRect().bottom) : clipTop;
  const gap = parseFloat(getComputedStyle(wrapper).getPropertyValue('--notif-card-gap')) || 0;
  const aboveTop = wrapperRect.top - gap - card.offsetHeight;
  wrapper.classList.toggle('open-above', aboveTop >= ceiling);
};

/**
 * Notification badge + hover card shown over the status badge in the documents list.
 *
 * @param {Array}  comments            - Array of comment objects { comment, userId, timestamp }
 * @param {Array}  violations          - Array of LMC contract violation enum strings
 * @param {boolean} isMultiplePatients - True when the upload may contain more than one patient's documents
 * @param {Array}  activeUsers         - Array of user objects { userId, name }
 */
const NotificationPreview = ({ comments = [], violations = [], isMultiplePatients = false, activeUsers = [] }) => {
  const wrapperRef = useRef(null);
  const scrollCleanupRef = useRef(null);
  const totalCount = comments.length + violations.length + (isMultiplePatients ? 1 : 0);

  useEffect(() => () => scrollCleanupRef.current?.(), []);

  if (totalCount === 0) return null;

  const clearScrollListener = () => {
    scrollCleanupRef.current?.();
    scrollCleanupRef.current = null;
  };

  const onMouseEnter = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }
    clearScrollListener();
    updateOpenAbove(wrapper);
    const scroll = wrapper.closest('.table-scroll-wrapper');
    if (!scroll) {
      return;
    }
    const onScroll = () => updateOpenAbove(wrapper);
    scroll.addEventListener('scroll', onScroll, { passive: true });
    scrollCleanupRef.current = () => scroll.removeEventListener('scroll', onScroll);
  };

  return (
    <div
      ref={wrapperRef}
      className="notif-preview-wrapper"
      onMouseEnter={onMouseEnter}
      onMouseLeave={clearScrollListener}
    >
      <span className="notif-count-badge">{totalCount}</span>

      <div className="notif-preview-card">
        <div className="notif-preview-body">

          {comments.length > 0 && (
            <div className="notif-section">
              <div className="notif-section-header">
                <span className="notif-section-label">Comments</span>
                <span className="notif-section-count count-blue">{comments.length}</span>
              </div>

              {comments.slice(0, 3).map((comment, index) => {
                const user = comment.userId === 'SYSTEM'
                  ? { name: 'MedPrompt App' }
                  : activeUsers.find(u => u.userId === comment.userId);
                const date = new Date(comment.timestamp);
                return (
                  <div key={index} className="notif-comment-item">
                    <div className="notif-comment-text">{comment.comment}</div>
                    <div className="notif-comment-meta">
                      <span className="notif-comment-author">{user?.name || 'Unknown User'}</span>
                      <span className="notif-comment-time">
                        {date.toLocaleDateString('en-GB')} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                );
              })}

              {comments.length > 3 && (
                <div className="notif-more">+{comments.length - 3} more comment{comments.length - 3 > 1 ? 's' : ''}</div>
              )}
            </div>
          )}

          {violations.length > 0 && (
            <div className="notif-section">
              <div className="notif-section-header">
                <span className="notif-section-label">Contract Violations</span>
                <span className="notif-section-count count-red">{violations.length}</span>
              </div>

              {violations.slice(0, 3).map((violation, index) => {
                const def = getViolationByValue(violation);
                return (
                  <div key={index} className="notif-violation-item">
                    <StatusBadge
                      label={def?.displayName ?? violation}
                      colorFamily="red"
                      size="small"
                      tooltip={def?.description}
                    />
                  </div>
                );
              })}

              {violations.length > 3 && (
                <div className="notif-more">+{violations.length - 3} more violation{violations.length - 3 > 1 ? 's' : ''}</div>
              )}
            </div>
          )}

          {isMultiplePatients && (
            <div className="notif-section notif-section-flag">
              <div className="notif-section-header">
                <span className="notif-section-label">Multiple patients</span>
                <span className="notif-section-count count-red">1</span>
              </div>
              <div className="notif-flag-text">
                This upload may contain documents for more than one patient.
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default NotificationPreview;
